import React from 'react';
import usePageMeta from '../usePageMeta';
import { LEGAL } from '../siteData';
import { useContact } from '../SiteContent';

/**
 * Datenschutzerklärung nach Art. 13 DSGVO.
 *
 * Beschrieben ist ausschließlich, was die Seite nachweislich tut — geprüft
 * gegen den Code, nicht aus einer Vorlage übernommen (zuletzt am 31.08.2026):
 *  - Umami-Analytics wird in `src/index.tsx` auf jeder Seite geladen.
 *  - Zusätzlich meldet die Kampagnenseite Ereignisse: `M38_EVENTS` in
 *    `website/miteinander/tracking.ts`, aufgerufen aus `Landing`, `M38Layout`,
 *    `AudioBeitraege`, `BetreuungsCheck` und `Danke`. Übertragen werden nur
 *    Ereignisname und Stelle der Seite, keine Formularinhalte.
 *  - Seit dem 06.09.2026 zählt auch die Kundenwebsite Bedienschritte:
 *    `SITE_EVENTS` in `website/tracking.ts` — Telefon-, WhatsApp- und
 *    E-Mail-Links über `useConversionTracking` (Pfad und Stelle), das
 *    abgeschickte Kontaktformular in `ContactForm` (Anliegen und Pfad).
 *  - Die Formulare senden per `website/publicApi.ts` an das eigene Backend
 *    und landen in der Tabelle `contact_requests` (keine IP-Spalte).
 *  - Die Absender-IP verarbeitet `ContactRequestService` nur flüchtig: als
 *    gesalzener Hash in `recentByIp` für die Ratenbegrenzung (5 pro Stunde),
 *    nie in der Datenbank. Die Dublettenprüfung vergleicht dagegen Nachricht,
 *    E-Mail und Telefon der letzten 10 Minuten in der Datenbank.
 *  - Die freiwilligen Zusatzfragen stehen in `website/fragebogen.ts` bzw. im
 *    Betreuungs-Check unter `website/miteinander/BetreuungsCheck.tsx`; darunter
 *    der Pflegegrad — eine Gesundheitsangabe nach Art. 9 Abs. 1 DSGVO.
 *  - Die Karte auf der Kontaktseite lädt Kacheln von openstreetmap.org; der
 *    Verweis darunter (`directionsUrl` in `siteData.ts`) führt zu Google Maps.
 *  - Schriften und Audiodateien liegen selbst gehostet unter `public/website/`.
 *  - Im Posteingang kommen Status, Zuweisung und interne Notiz hinzu
 *    (`ContactRequestRepository`); gelöscht wird von Hand über die Oberfläche —
 *    einen automatischen Löschlauf gibt es bewusst nicht.
 *
 * Dieselbe Erklärung gilt für beide Auftritte (siehe `website/sites.json`):
 * die Unternehmenswebsite und die Kampagnenseite miteinander38.de. Sie laufen
 * in derselben Anwendung auf demselben Server und verarbeiten Anfragen gleich.
 *
 * Wer eines dieser Verhalten ändert, muss diesen Text mitziehen.
 */
const Datenschutz: React.FC = () => {
  const contact = useContact();

  usePageMeta(
    'Datenschutzerklärung',
    `Wie die ${contact.company} personenbezogene Daten auf dieser Website verarbeitet.`,
  );

  return (
    <section className="shell shell--prose" style={{ paddingTop: 64, paddingBottom: 60 }}>
      <h1 className="h-page" style={{ marginTop: 0 }}>
        Datenschutzerklärung
      </h1>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        1. Verantwortlicher
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        {contact.company}
        <br />
        Inhaber: {LEGAL.owner}
        <br />
        {contact.street}, {contact.zip} {contact.city}
        <br />
        Telefon: <a href={contact.phoneHref}>{contact.phoneLabel}</a>
        <br />
        E-Mail: <a href={contact.emailHref}>{contact.email}</a>
      </p>
      {LEGAL.dataProtectionOfficer && (
        <p style={{ lineHeight: 1.8 }}>
          Datenschutzbeauftragte(r): {LEGAL.dataProtectionOfficer}
        </p>
      )}
      <p style={{ lineHeight: 1.8 }}>
        Diese Erklärung gilt für unsere Unternehmenswebsite unter{' '}
        <strong>sb-antczak.de</strong> ebenso wie für unsere Kampagnenseite{' '}
        <strong>miteinander38.de</strong>. Beide werden von derselben Anwendung auf demselben
        Server ausgeliefert und verarbeiten Ihre Angaben auf dieselbe Weise.
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        2. Aufruf der Website (Server-Logfiles)
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Beim Aufruf dieser Website protokolliert unser Webserver technisch notwendige
        Zugriffsdaten: IP-Adresse, Datum und Uhrzeit, aufgerufene Seite, übertragene Datenmenge,
        Browsertyp und Betriebssystem. Diese Verarbeitung ist erforderlich, um die Seite
        auszuliefern, ihre Stabilität zu sichern und Angriffe zu erkennen.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Rechtsgrundlage ist unser berechtigtes Interesse am sicheren Betrieb der Website
        (Art. 6 Abs. 1 lit. f DSGVO). Die Protokolle werden nach kurzer Zeit automatisch gelöscht
        und nicht mit anderen Daten zusammengeführt.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Die Website wird für uns bei der Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen,
        auf Servern in Deutschland betrieben. Mit dem Anbieter besteht ein Vertrag zur
        Auftragsverarbeitung nach Art. 28 DSGVO.
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        3. Kontakt-, Bewerbungsformular und Betreuungs-Check
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Wenn Sie eines der Formulare auf dieser Website absenden, werden Ihre Angaben an unseren
        Server übertragen und dort gespeichert, damit wir Ihre Anfrage bearbeiten können.
        Verarbeitet werden: Vor- und Nachname, E-Mail-Adresse, Telefonnummer, Ihre Nachricht sowie
        das Thema und die Seite, von der die Anfrage abgeschickt wurde. Pflichtangabe ist allein
        Ihre Nachricht — alles Weitere ist freiwillig, erleichtert uns aber die Antwort. Im
        Betreuungs-Check auf miteinander38.de treten an die Stelle der Nachricht Ihr Vorname und
        Ihre Telefonnummer, weil wir Sie dort zurückrufen sollen.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        <strong>Freiwillige Zusatzfragen.</strong> Geht es um eine Betreuung — auf den Seiten zur
        24-Stunden-Betreuung und zur Hauswirtschaft sowie im Betreuungs-Check auf
        miteinander38.de — stellen wir Ihnen einige kurze Fragen: für wen Unterstützung gesucht
        wird, wie viele Personen im Haushalt leben, wie schnell Unterstützung gebraucht wird, ob
        ein Pflegegrad vorliegt und in welchem Ort beziehungsweise Postleitzahlgebiet die
        Betreuung stattfinden soll. <strong>Alle diese Angaben sind freiwillig.</strong> Wir
        übermitteln nur die Fragen, die Sie tatsächlich beantwortet haben; eine Anfrage ohne eine
        einzige Antwort geht genauso an uns wie eine vollständige. Bei einer Bewerbung werden
        diese Fragen weder gestellt noch entgegengenommen.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        <strong>Angabe zum Pflegegrad.</strong> Ein Pflegegrad ist eine Angabe über die
        Gesundheit und damit eine besondere Kategorie personenbezogener Daten im Sinne von
        Art. 9 Abs. 1 DSGVO. Wir verarbeiten sie ausschließlich auf Grundlage Ihrer
        ausdrücklichen Einwilligung nach Art. 9 Abs. 2 lit. a DSGVO. Deshalb bestätigen Sie die
        Einwilligung im Formular gesondert per Häkchen, bevor die Angaben abgeschickt werden.
        Ohne dieses Häkchen können Sie uns weiterhin schreiben — nur eben ohne die freiwilligen
        Zusatzangaben. Zweck ist allein, Ihre Situation vor dem ersten Gespräch einordnen und Sie
        passend beraten zu können. Sie können die Einwilligung jederzeit mit Wirkung für die
        Zukunft widerrufen (siehe Abschnitt 8); die Rechtmäßigkeit der bis dahin erfolgten
        Verarbeitung bleibt davon unberührt.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Betrifft Ihre Anfrage eine andere Person — etwa Ihre Mutter oder Ihren Vater —, geben Sie
        deren Angaben bitte nur an, wenn Sie dazu berechtigt sind und die betroffene Person damit
        einverstanden ist.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        <strong>Schutz vor automatisierten Einsendungen.</strong> Das Formular enthält ein
        verstecktes Feld, das für Sie unsichtbar bleibt und nur von Programmen ausgefüllt wird.
        Zusätzlich begrenzen wir die Zahl der Einsendungen je Absender: Dafür wird Ihre
        IP-Adresse beim Absenden nicht im Klartext verwendet, sondern sofort in einen
        unumkehrbaren Prüfwert umgerechnet, der ausschließlich im Arbeitsspeicher unseres Servers
        steht und spätestens nach einer Stunde verfällt. <strong>Zu Ihrer Anfrage gespeichert
        wird die IP-Adresse nicht</strong> — sie steht weder in der Datenbank noch im
        Posteingang. Damit eine versehentlich zweimal abgeschickte Anfrage nicht doppelt
        im Posteingang landet, gleichen wir sie außerdem mit den Anfragen der letzten zehn
        Minuten ab und verwerfen die Wiederholung. Rechtsgrundlage ist unser
        berechtigtes Interesse an einem funktionsfähigen, nicht durch Spam blockierten
        Kontaktweg (Art. 6 Abs. 1 lit. f DSGVO).
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Ihre Anfrage ist ausschließlich für unsere Mitarbeiterinnen und Mitarbeiter im internen
        Anfragen-Posteingang sichtbar; eine Weitergabe an Dritte findet nicht statt. Zur
        Bearbeitung halten wir dort zusätzlich fest, welchen Stand die Anfrage hat, wer sie
        übernommen hat und — soweit für die Beratung nötig — eine interne Notiz. Auch diese
        Angaben werden mit der Anfrage gelöscht.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, soweit Ihre Anfrage auf den Abschluss oder
        die Durchführung eines Vertrags gerichtet ist, im Übrigen unser berechtigtes Interesse an
        der Beantwortung von Anfragen (Art. 6 Abs. 1 lit. f DSGVO). Für die freiwilligen
        Zusatzangaben tritt Ihre Einwilligung hinzu (Art. 6 Abs. 1 lit. a und, für die Angabe zum
        Pflegegrad, Art. 9 Abs. 2 lit. a DSGVO). Bei Bewerbungen ist
        Rechtsgrundlage § 26 Abs. 1 BDSG. Wir löschen die Angaben, sobald Ihr Anliegen
        abschließend bearbeitet ist und keine gesetzlichen Aufbewahrungspflichten entgegenstehen;
        Bewerbungsunterlagen spätestens sechs Monate nach Abschluss des Verfahrens.
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        4. Reichweitenmessung mit Umami
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Um zu verstehen, welche Inhalte gelesen werden, nutzen wir Umami — eine
        Analyse-Software, die wir auf einem eigenen Server betreiben. Es werden keine Daten an
        Dritte weitergegeben und keine Profile über Websites hinweg gebildet.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Umami setzt <strong>keine Cookies</strong> und speichert nichts auf Ihrem Gerät. Erfasst
        werden die aufgerufene Seite, die verweisende Seite, Browser, Betriebssystem, ungefähre
        Herkunft auf Länderebene und die Bildschirmgröße. Ihre IP-Adresse wird dabei nicht
        gespeichert. Rechtsgrundlage ist unser berechtigtes Interesse an einer
        datensparsamen Reichweitenmessung (Art. 6 Abs. 1 lit. f DSGVO).
      </p>
      <p style={{ lineHeight: 1.8 }}>
        <strong>Gezählte Bedienschritte.</strong> Zusätzlich zählen wir einige Schritte, weil ein
        reiner Seitenaufruf nichts darüber sagt, ob die Seite geholfen hat: auf dieser Website das
        Antippen der Telefonnummer, des WhatsApp-Links und der E-Mail-Adresse sowie das Absenden
        des Kontaktformulars (mit dem gewählten Anliegen); auf miteinander38.de das Antippen der
        Telefonnummer, das Anklicken der Schaltfläche zum Betreuungs-Check, das Abspielen eines
        Radiobeitrags, das Aufklappen eines Transkripts sowie das Beginnen und das Absenden des
        Betreuungs-Checks. Gezählt wird jeweils nur, <em>dass</em> und auf welcher Seite bzw. an
        welcher Stelle etwas geschehen ist —{' '}
        <strong>keine Inhalte, die Sie eingeben, und nichts, was zu Ihrer Person zurückführt</strong>.
        Der Inhalt des Kontaktformulars und des Betreuungs-Checks fließt nicht in die Messung ein,
        sondern ausschließlich in die Anfrage nach Abschnitt 3. Rechtsgrundlage ist ebenfalls
        Art. 6 Abs. 1 lit. f DSGVO.
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        5. Kartenansicht auf der Kontaktseite
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Auf der Kontaktseite zeigen wir eine Karte mit Kartenmaterial von OpenStreetMap. Beim
        Laden der Karte übermittelt Ihr Browser Ihre IP-Adresse an die OpenStreetMap Foundation,
        St John’s Innovation Centre, Cowley Road, Cambridge CB4 0WS, Vereinigtes Königreich.
        Für das Vereinigte Königreich liegt ein Angemessenheitsbeschluss der Europäischen
        Kommission vor. Rechtsgrundlage ist unser berechtigtes Interesse an einer verständlichen
        Anfahrtsbeschreibung (Art. 6 Abs. 1 lit. f DSGVO). Datenschutzerklärung der Stiftung:{' '}
        <a href="https://osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer">
          osmfoundation.org/wiki/Privacy_Policy
        </a>
        .
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        6. Schriften und Bilder
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Alle Schriftarten und Bilder liefern wir vom eigenen Server aus. Eine Verbindung zu Google
        Fonts oder anderen externen Content-Netzwerken findet nicht statt.
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        7. Verlinkte Profile und Messenger
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Im Fußbereich verlinken wir unsere Profile bei Facebook, Instagram und LinkedIn sowie
        einen WhatsApp-Kontakt. Es handelt sich um einfache Links — es werden keine Inhalte dieser
        Anbieter auf unserer Seite eingebunden, und es fließen erst dann Daten dorthin, wenn Sie
        einen Link anklicken. Ab diesem Zeitpunkt gelten die Datenschutzbestimmungen des jeweiligen
        Anbieters. Wenn Sie uns über WhatsApp schreiben, verarbeitet die WhatsApp Ireland Ltd.
        Ihre Telefonnummer und die Nachrichteninhalte.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Ebenso verhält es sich mit dem Verweis „Anfahrt mit Google Maps planen" unter der Karte
        auf der Kontaktseite: Er ist ein gewöhnlicher Link auf Google Maps, kein eingebetteter
        Inhalt.
        Erst wenn Sie ihn anklicken, öffnet sich Google Maps mit unserer Anschrift als Ziel, und
        Ihr Browser übermittelt dabei Ihre IP-Adresse an die Google Ireland Limited, Gordon
        House, Barrow Street, Dublin 4, Irland. Ob und welche weiteren Daten dort verarbeitet
        werden, richtet sich nach Ihren Einstellungen bei Google und dessen
        Datenschutzerklärung:{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
          policies.google.com/privacy
        </a>
        . Die Anfahrt steht auf der Kontaktseite auch als Text — Sie sind auf den Link nicht
        angewiesen.
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        8. Ihre Rechte
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Sie haben das Recht auf Auskunft über die zu Ihrer Person gespeicherten Daten (Art. 15
        DSGVO), auf Berichtigung (Art. 16), auf Löschung (Art. 17), auf Einschränkung der
        Verarbeitung (Art. 18), auf Datenübertragbarkeit (Art. 20) sowie auf Widerspruch gegen
        Verarbeitungen, die auf einem berechtigten Interesse beruhen (Art. 21). Eine erteilte
        Einwilligung können Sie jederzeit mit Wirkung für die Zukunft widerrufen.
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Wenden Sie sich dafür formlos an <a href={contact.emailHref}>{contact.email}</a>.
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        9. Beschwerderecht
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Sie können sich jederzeit bei einer Datenschutz-Aufsichtsbehörde beschweren. Für uns
        zuständig ist:
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Die Landesbeauftragte für den Datenschutz Niedersachsen
        <br />
        Prinzenstraße 5, 30159 Hannover
        <br />
        <a href="https://www.lfd.niedersachsen.de" target="_blank" rel="noopener noreferrer">
          lfd.niedersachsen.de
        </a>
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        10. Verschlüsselung und automatisierte Entscheidungen
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Diese Website wird ausschließlich verschlüsselt über HTTPS ausgeliefert. Eine
        automatisierte Entscheidungsfindung einschließlich Profiling nach Art. 22 DSGVO findet
        nicht statt. Sie sind nicht verpflichtet, uns personenbezogene Daten bereitzustellen —
        ohne Ihre Angaben können wir eine Anfrage jedoch nicht beantworten.
      </p>

      <p className="note" style={{ marginTop: 40 }}>
        Stand: {LEGAL.lastUpdated}
      </p>
    </section>
  );
};

export default Datenschutz;
